import type { ResponseMode, VendorRfqBase } from "../types";
import { declineReasonLabel } from "../constants/declineReasons";
import { rfqDetailPath } from "../constants/paths";
import { formatDay, formatRemaining, formatRespondBy } from "./deadline";
import { declineOf, submissionOf } from "./response";

export interface RfqCardAction {
  label: string;
  to: string;
}

export interface RfqCardCopy {
  headline: string;
  countdown?: string;
  mode?: string;
  windowOpen: boolean;
  action: RfqCardAction;
}

const HOUR = 60 * 60 * 1000;
const MODE_LABELS: Record<ResponseMode, string> = { ACCEPT_AT_ASK: "At asking price", BID: "Bid" };

export const projectLine = (rfq: Pick<VendorRfqBase, "project_name" | "city">): string => `${rfq.project_name} · ${rfq.city}`;

export const responseModeLabel = (mode: ResponseMode): string => MODE_LABELS[mode];

function windowHours(rfq: Pick<VendorRfqBase, "floated_at" | "respond_by">): number | null {
  if (!rfq.floated_at) return null;
  const hours = Math.round((new Date(rfq.respond_by).getTime() - new Date(rfq.floated_at).getTime()) / HOUR);
  return hours > 0 ? hours : null;
}

export function describeRfq(rfq: VendorRfqBase, nowMs: number, timeZone?: string): RfqCardCopy {
  const remaining = rfq.clock.state === "RUNNING" ? new Date(rfq.respond_by).getTime() - nowMs : 0;
  const windowOpen = remaining > 0;
  const view: RfqCardAction = { label: "View RFQ", to: rfqDetailPath(rfq.id) };
  const submission = submissionOf(rfq);
  const mode = submission ? responseModeLabel(submission.mode) : undefined;

  switch (rfq.status) {
    case "OPEN": {
      const hours = windowHours(rfq);
      return {
        headline: `Respond by ${formatRespondBy(rfq.respond_by, timeZone)}${hours ? ` · ${hours}h window` : ""}`,
        countdown: formatRemaining(remaining),
        windowOpen,
        action: view,
      };
    }
    case "SUBMITTED":
      return {
        headline: `Under evaluation · decision by ${formatDay(submission?.decision_expected_by, timeZone)}`,
        mode,
        countdown: windowOpen ? formatRemaining(remaining) : undefined,
        windowOpen,
        action: { label: windowOpen ? "Withdraw & edit" : "View response", to: rfqDetailPath(rfq.id) },
      };
    case "ALLOTTED":
      return { headline: "Allotted · PO to follow", mode, windowOpen: false, action: view };
    case "EXPIRED":
      return { headline: "Expired · Window closed", windowOpen: false, action: view };
    case "DECLINED": {
      const reason = declineReasonLabel(declineOf(rfq)?.decline_reason);
      return { headline: reason ? `Declined · ${reason}` : "Declined", windowOpen: false, action: view };
    }
    default:
      return { headline: "Closed", windowOpen: false, action: view };
  }
}
